// src/belediye/pages/sistem/SistemVatandaslarPage.js
//
// FAZ-24 BLOK-C.3 — Vatandaşlar (KVKK) yönetim ekranı: liste + SUNUCU-tarafı arama
// (ad/soyad/telefon = q) + sayfalama (page_size=25) + yeni vatandaş. Detay ayrı
// sayfada (/vatandaslar/:citizenId). PII olduğu için client'ta tüm liste tutulmaz.
// Gate: citizens.view (route), citizens.create (aksiyon).

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PlusIcon, MagnifyingGlassIcon, UserIcon } from "@heroicons/react/24/outline";

import usePermissions from "../../hooks/usePermissions";
import { fetchCitizens, createCitizen } from "../../services/yonetimApi";
import "./yonetim-ekrani.css";

const PAGE_SIZE = 25;

const backendError = (ex, fallback) => {
  const d = ex?.response?.data;
  if (typeof d === "string") return d;
  if (d?.detail) return d.detail;
  if (d) {
    return Object.entries(d)
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
      .join(" · ");
  }
  return fallback;
};

export default function SistemVatandaslarPage() {
  const { can } = usePermissions();
  const navigate = useNavigate();
  const canCreate = can("citizens.create");

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  const reqRef = useRef(0); // yarış koşulu koruması (son istek kazanır)

  const load = useCallback(async (pageArg, qArg) => {
    const ticket = ++reqRef.current;
    setLoading(true);
    setError("");
    try {
      const data = await fetchCitizens({ page: pageArg, q: qArg, page_size: PAGE_SIZE });
      if (ticket !== reqRef.current) return; // eski istek → yok say
      setRows(Array.isArray(data?.results) ? data.results : []);
      setCount(Number(data?.count) || 0);
    } catch {
      if (ticket !== reqRef.current) return;
      setError("Vatandaşlar yüklenemedi.");
    } finally {
      if (ticket === reqRef.current) setLoading(false);
    }
  }, []);

  // arama debounce: q değişince 1. sayfaya dön
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      load(1, q);
    }, 300);
    return () => clearTimeout(t);
  }, [q, load]);

  const goPage = (p) => {
    setPage(p);
    load(p, q);
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <div className="page-container ye-root">
      <header className="ye-head">
        <div>
          <h1 className="ye-title">Vatandaşlar</h1>
          <p className="ye-subtitle">
            Vatandaş kayıtlarını arayın ve yönetin. Bu ekran KVKK kapsamındadır;
            her erişim kayıt altına alınır.
          </p>
        </div>
        {canCreate && (
          <button
            className="btn-md btn-md-primary ye-new-btn"
            onClick={() => setShowCreate(true)}
          >
            <PlusIcon width={18} height={18} />
            Yeni Vatandaş
          </button>
        )}
      </header>

      <div className="ye-filters">
        <div className="ye-search">
          <MagnifyingGlassIcon width={18} height={18} className="ye-search-icon" />
          <input
            className="md-input ye-search-input"
            placeholder="Ad, soyad veya telefon ara"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Vatandaş ara"
          />
        </div>
      </div>

      {loading ? (
        <div className="ye-state">Yükleniyor…</div>
      ) : error ? (
        <div className="ye-state ye-state-error">
          {error}
          <button className="btn-md btn-md-ghost" onClick={() => load(page, q)}>
            Tekrar dene
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="ye-state">
          {q.trim() ? "Eşleşen vatandaş bulunamadı." : "Henüz vatandaş kaydı yok."}
        </div>
      ) : (
        <>
          <ul className="ye-list">
            {rows.map((c) => (
              <li key={c.id}>
                <button
                  className="md-card ye-item"
                  onClick={() => navigate(`/yonetim/sistem/vatandaslar/${c.id}`)}
                >
                  <span className="ye-item-main">
                    <span className="ye-item-name">
                      <UserIcon
                        width={15}
                        height={15}
                        style={{ verticalAlign: "-2px", marginRight: 6 }}
                      />
                      {c.name} {c.surname}
                    </span>
                    <span className="ye-item-sub">{c.phone_number}</span>
                  </span>
                  <span className="ye-item-meta">
                    {c.email ? <span className="md-chip">{c.email}</span> : null}
                  </span>
                </button>
              </li>
            ))}
          </ul>

          {totalPages > 1 && (
            <div className="ye-modal-actions ye-modal-actions-split">
              <button
                className="btn-md btn-md-ghost"
                onClick={() => goPage(page - 1)}
                disabled={page <= 1}
              >
                ‹ Önceki
              </button>
              <span className="ye-hint">
                Sayfa {page} / {totalPages} · {count} kayıt
              </span>
              <button
                className="btn-md btn-md-ghost"
                onClick={() => goPage(page + 1)}
                disabled={page >= totalPages}
              >
                Sonraki ›
              </button>
            </div>
          )}
        </>
      )}

      {showCreate && (
        <VatandasOlusturModal
          onClose={() => setShowCreate(false)}
          onCreated={(created) => {
            setShowCreate(false);
            if (created?.id) navigate(`/yonetim/sistem/vatandaslar/${created.id}`);
            else load(page, q);
          }}
        />
      )}
    </div>
  );
}

function VatandasOlusturModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", surname: "", phone_number: "", email: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    // REVIEW #10: backend create 10-hane zorlamıyor → client'ta doğrula (KVKK).
    if (!/^\d{10}$/.test(String(form.phone_number).trim())) {
      setErr("Telefon 10 haneli, yalnız rakam olmalı.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, phone_number: String(form.phone_number).trim() };
      if (!payload.email) delete payload.email;
      onCreated(await createCitizen(payload));
    } catch (ex) {
      setErr(backendError(ex, "Vatandaş oluşturulamadı."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ye-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="ye-modal md-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="md-card-header">
          <h2 className="md-card-title">Yeni Vatandaş</h2>
        </div>
        <form className="ye-form" onSubmit={submit} autoComplete="off">
          {err && <div className="ye-alert">{err}</div>}
          <div className="ye-field-row">
            <label className="ye-field">
              <span>Ad</span>
              <input className="md-input" value={form.name} onChange={set("name")} required autoFocus />
            </label>
            <label className="ye-field">
              <span>Soyad</span>
              <input className="md-input" value={form.surname} onChange={set("surname")} required />
            </label>
          </div>
          <label className="ye-field">
            <span>Telefon (10 hane)</span>
            <input
              className="md-input"
              value={form.phone_number}
              onChange={set("phone_number")}
              inputMode="numeric"
              placeholder="5xxxxxxxxx"
              required
            />
          </label>
          <label className="ye-field">
            <span>E-posta (opsiyonel)</span>
            <input className="md-input" type="email" value={form.email} onChange={set("email")} />
          </label>
          <div className="ye-modal-actions">
            <button type="button" className="btn-md btn-md-ghost" onClick={onClose} disabled={saving}>
              Vazgeç
            </button>
            <button type="submit" className="btn-md btn-md-primary" disabled={saving}>
              {saving ? "Oluşturuluyor…" : "Oluştur"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
